/**
 * 로컬 스토리지 래퍼
 *
 * localStorage/cookie 접근을 추상화하여 안전하게 데이터를 저장/조회합니다.
 */

/** 스토리지 인터페이스 */
export interface StorageAdapter {
  get(key: string): string | null;
  set(key: string, value: string, expiryDays?: number): void;
  remove(key: string): void;
}

/** LocalStorage 어댑터 (SSR-safe) */
export class LocalStorageAdapter implements StorageAdapter {
  private available: boolean;

  constructor() {
    this.available = this.checkAvailability();
  }

  private checkAvailability(): boolean {
    try {
      if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return false;
      }
      const testKey = '__la_test__';
      localStorage.setItem(testKey, '1');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  get(key: string): string | null {
    if (!this.available) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  set(key: string, value: string, _expiryDays?: number): void {
    if (!this.available) return;
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage full or blocked — silently fail
    }
  }

  remove(key: string): void {
    if (!this.available) return;
    try {
      localStorage.removeItem(key);
    } catch {
      // Silently fail
    }
  }
}

/** SessionStorage 어댑터 (SSR-safe) */
export class SessionStorageAdapter implements StorageAdapter {
  private available: boolean;

  constructor() {
    this.available = this.checkAvailability();
  }

  private checkAvailability(): boolean {
    try {
      if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
        return false;
      }
      const testKey = '__la_test__';
      sessionStorage.setItem(testKey, '1');
      sessionStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  get(key: string): string | null {
    if (!this.available) return null;
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  set(key: string, value: string, _expiryDays?: number): void {
    if (!this.available) return;
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Silently fail
    }
  }

  remove(key: string): void {
    if (!this.available) return;
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Silently fail
    }
  }
}

/** Cookie 어댑터 (SSR-safe) */
export class CookieAdapter implements StorageAdapter {
  get(key: string): string | null {
    if (typeof document === 'undefined') return null;
    try {
      const match = document.cookie.match(new RegExp(`(?:^|; )${this.escapeRegex(key)}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  }

  set(key: string, value: string, expiryDays?: number): void {
    if (typeof document === 'undefined') return;
    try {
      let cookie = `${key}=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
      if (expiryDays !== undefined && expiryDays > 0) {
        const expires = new Date(Date.now() + expiryDays * 86400000);
        cookie += `; expires=${expires.toUTCString()}`;
      }
      document.cookie = cookie;
    } catch {
      // Silently fail
    }
  }

  remove(key: string): void {
    if (typeof document === 'undefined') return;
    try {
      document.cookie = `${key}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    } catch {
      // Silently fail
    }
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
